import React, { useEffect, useRef } from "react";

import { PopoverFilterList } from "@/components/molecules/PopoverFilterList";
import { type IWidgetFilterItem, useWidgetFiltersController } from "./useWidgetFiltersController";



export interface IDimensionFilterProps {
    items: IWidgetFilterItem[];
    controller: ReturnType<typeof useWidgetFiltersController>;
    allSelectedByDefault?: boolean;
    filterCTA?: string;
    /** Initial selection to seed on first load (e.g. the campaigns a configured filter matches). */
    initialSelectedItems?: IWidgetFilterItem[];
    /**
     * True when a campaign/dimension data-filter is configured on the widget. When set, the match
     * set is authoritative even if empty: an empty `initialSelectedItems` means "the filter matched
     * no campaigns" -> seed an empty selection rather than falling back to all items.
     */
    hasConfiguredFilter?: boolean;
    /**
     * Identity (field/operator/value) of the widget's configured data-level filters. When it changes
     * the filter was edited, so the campaign pre-selection is re-seeded to the new match instead of
     * leaving a stale selection from the previous filter (LAD-452).
     */
    filterSignature?: string;
}

export const DimensionFilter: React.FC<IDimensionFilterProps> = ({
    items,
    controller,
    allSelectedByDefault = true,
    filterCTA = "Filter",
    initialSelectedItems,
    hasConfiguredFilter = false,
    filterSignature = "",
}) => {
    const { selectedDimensionItems, setSelectedDimensionItems, setDimensionFilterActive } = controller;
    const firstLoad = useRef<boolean>(true);
    const appliedFilterSignature = useRef<string | null>(null);

    const handleSelectionChange = (newItems: IWidgetFilterItem[]) => {
        setSelectedDimensionItems(newItems);
    };

    // The selection we'd apply given the current filter + available items. With a configured filter,
    // seed exactly the campaigns it matches - even none (LAD-454). Otherwise default to all items.
    const seedItems = hasConfiguredFilter ? (initialSelectedItems ?? []) : items;

    // Tell the table consumers a configured filter is driving the selection, so an empty selection
    // is read as "show none" rather than "no filter -> show all".
    useEffect(() => {
        setDimensionFilterActive(hasConfiguredFilter);
    }, [hasConfiguredFilter, setDimensionFilterActive]);

    useEffect(() => {
        // Wait until options are available so a seed isn't lost on an empty first render.
        if (items.length === 0) return;

        if (firstLoad.current) {
            // Always re-seed when there's a configured filter — the component may have unmounted and
            // remounted while data was loading (hasRowsToFilter → false), during which the filter
            // definition may have changed. The controller state persists across remounts so the old
            // selection would otherwise survive the remount unchecked (LAD-xxx).
            const noCurrentSelection = !selectedDimensionItems || selectedDimensionItems.length === 0;
            if (noCurrentSelection || hasConfiguredFilter) {
                setSelectedDimensionItems(seedItems, true);
            }
            appliedFilterSignature.current = filterSignature;
            firstLoad.current = false;
            return;
        }

        // The configured data-level filter changed -> re-seed the campaign selection to the new match
        // so a stale pre-selection from the previous filter doesn't persist (LAD-452). The filter is
        // the source of truth for what's pre-selected, so this overrides the previous selection.
        // Deliberately NOT gated on "user interacted": that flag is shared across all filter types
        // (product, service, ...) and gets set on load, which would suppress the required reset.
        if (appliedFilterSignature.current !== null && appliedFilterSignature.current !== filterSignature) {
            setSelectedDimensionItems(seedItems, true);
            appliedFilterSignature.current = filterSignature;
        }
    }, [items, seedItems, filterSignature, hasConfiguredFilter, setSelectedDimensionItems, selectedDimensionItems]);

    return (
        <PopoverFilterList
            items={items}
            selectedItems={selectedDimensionItems}
            onSelectionChange={handleSelectionChange}
            triggerLabel={filterCTA}
            allSelectedByDefault={hasConfiguredFilter ? false : allSelectedByDefault}
            selectAllLabel={selectedDimensionItems.length === items.length ? 'Unselect All' : 'Select All'}
        />
    );
};
